import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useEffect } from "react"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import MembershipTypeCode from "../../enums/membershipTypeCode.enum"
import { listProductTypes } from '../../services/productType.service'
import { listMembershipTypes } from '../../services/membershipType.service'
import LeqvioIntro  from "./leqvioIntro.component"
import { completeFlow } from "../../services/flow.service"
import FlowType from "../../enums/flowType.enum"
import { useNavigate } from "react-router-dom"
import MembershipHelper from "../../helpers/membership.helper"

export const Step = {
  INTRO: 'intro',
  ACCOUNT: 'account',
  MEMBERSHIP: "membership",
  PAY: 'pay',
  NAME: 'name',
  GENDER: 'gender',
  DOB: 'dob',
  LOCATION: 'location',
  DELIVERY: 'delivery',
  PHONE: 'phone',
  CONFIRM: 'confirm',
}

export const LeqvioFlow = () => {
  const navigate = useNavigate()
  const { instalabMembership } = useContext(UserContext)
  const [membershipTypes, setMembershipTypes] = useState()
  const [productTypes, setProductTypes] = useState()
  const [skeleton, setSkeleton] = useState()
  const [flow, setFlow] = useState()
  const [hasPass, setHasPass] = useState()

  const [initialUser, setInitialUser] = useState()

  useEffect(() => {
    fetchSkeleton()
  }, [hasPass, initialUser, membershipTypes, productTypes, instalabMembership, flow])

  useEffect(() => {
    fetchProductTypes()
    fetchMembershipTypes()
  }, [])

  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }
  const getMembershipType = (code) => {
    return membershipTypes?.find(membershipType => membershipType.code === code)
  }

  const fetchMembershipTypes = async () => {
    const response = await listMembershipTypes({
      filter: {
        code: {
          $in: [
            MembershipTypeCode.HEART_HEALTH,
          ]
        }
      }
    })
    setMembershipTypes(response)
  }
  
  const fetchProductTypes = async () => {
    const response = await listProductTypes({
      filter: {
        code: {
          $in: [
            ProductTypeCode.LEQVIO
          ]
        }
      }
    })
    setProductTypes(response)
  }

  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser) return

    setSkeleton({
      [Step.INTRO]: {
        type: StepType.CUSTOM,
        nextStep: Step.ACCOUNT,
        content: ({ footer }) => <LeqvioIntro footer={footer}/>
      },
      [Step.ACCOUNT]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.MEMBERSHIP,
        buildUser: true,
        title: `First, let's create your Instalab acccount.`,
        description: <>This will help you manage orders, test results and treatments. Already have an account? <a className="secondary-link" onClick={() => navigate(`/login?redirect=/flow/${FlowType.LEQVIO}`)}>Log in</a>.</>, 
        fields: [{
          name: 'email',
          label: 'Email Address',
          placeholder: 'Type your email here...',
          email: true,
          required: true,
          unique: true,
        }, {
          name: 'password',
          label: 'Password',
          placeholder: 'Type your password here...',
          password: true,
          required: true,
        }],
        skipIf: () => {
          return hasPass
        }
      },
      [Step.MEMBERSHIP]: {
        type: StepType.PAY,
        nextStep: Step.PAY,
        addSubscription: true,
        subscriptionType: MembershipTypeCode.HEART_HEALTH,
        title: <>Pay <span className="true-price">${getMembershipType(MembershipTypeCode.HEART_HEALTH)?.cost}/month</span> for your Heart Health membership.</>,
        description: <>Heart Health membership is required for access to this medication. Other benefits including frequent testing to verify efficacy of treatments and consults with heart health specialists. Cancel anytime.</>,
        skipIf: () => {
          return MembershipHelper.isHeartHealth(instalabMembership) || MembershipHelper.isUltimate(instalabMembership)
        }
      },
      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.NAME,
        addProduct: true,
        productType: ProductTypeCode.LEQVIO,
        title: <>Pay <span className="true-price">${getProductType(ProductTypeCode.LEQVIO)?.cost}</span> for the {getProductType(ProductTypeCode.LEQVIO)?.title}.</>,
        description: `This includes the medication delivered to you and a visit by a mobile nurse to administer the treatment.`,
      },
      [Step.NAME]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.GENDER,
        buildUser: true,
        title: `What's your full name?`,
        description: `We need this for your prescription.`,
        fields: [{
          name: 'firstName',
          placeholder: 'First Name',
          required: true,
        }, {
          name: 'lastName',
          placeholder: 'Last Name',
          required: true,
        }],
        skipIf: () => {
          return initialUser?.firstName && initialUser?.lastName
        }
      },
      [Step.GENDER]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.DOB,
        buildUser: true,
        title: `What's your biological sex?`,
        field: {
          name: 'gender',
          options: [{
            label: 'Male',
            value: Gender.MALE,
          }, {
            label: 'Female',
            value: Gender.FEMALE,
          }]
        },
        skipIf: () => {
          return initialUser?.gender
        }
      },
      [Step.DOB]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.LOCATION,
        buildUser: true,
        title: `What's your date of birth?`,
        field: {
          name: 'dob',
          placeholder: 'MM/DD/YYYY',
          date: true,
          inputMode: 'numeric',
          required: true,
        },
        skipIf: () => {
          return initialUser?.dob
        }
      },
      [Step.LOCATION]: {
        type: StepType.LOCATION,
        buildUser: true,
        nextStep: Step.DELIVERY,
        title: `What's your physical address?`,
        description: `This is where the nurse will meet you to administer your medication.`,
        field: {
          name: 'location'
        },
      },

      [Step.DELIVERY]: {
        type: StepType.LOCATION,
        buildUser: true,
        nextStep: Step.PHONE,
        title: `What's your delivery address?`,
        description: `We’ll send your medication to this location. You just need to give to the mobile nurse when they arrive.`,
        field: {
          name: 'shippingLocation'
        },
      },

      [Step.PHONE]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `What's your phone number?`,
        description: `We'll only text you urgent notifications about your account or results`,
        field: {
          name: 'phone',
          phone: true,
          inputMode: 'numeric',
          placeholder: 'Type your phone number here...',
          required: true,
          unique: true,
        },
        skipIf: () => {
          return initialUser?.phone
        }
      },

      [Step.CONFIRM]: {
        type: StepType.STATIC,
        showFooter: false,
        title: `Confirmed!`,
        description: `Your medication should arrive to you within 1 week. A member of the Instalab concierge team will be in touch within 1 business day to coordinate an at-home visit for our nurse to come administer the treatment.`,
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        // content: <LeqvioConfirm/>,
        onLoad: async () => {
          if (flow?._id) {
            await completeFlow(flow._id)
          }
        }
      }
    })
  }

  return (
    <Flow 
      skeleton={skeleton} 
      flow={flow} 
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      startIndex={0}
    />
  )
}